/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { useFormikContext } from 'formik'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  PLUGIN_OPTIONAL_COLOUR_GROUPS,
  PLUGIN_PAGE_COLOUR_GROUPS,
  PLUGIN_SCOREBOARD_OPTIONS,
  PLUGIN_THEME_MODE_OPTIONS,
  isPrimaryHardToSeeOnHeader,
  pluginPageDefaults,
  type PluginBrand,
  type PluginEmbedSettings,
  type PluginOptionalColourGroup,
  type PluginThemeMode,
} from '@sportninja/common/features/account/apiAccess'
import { SegmentedInput } from 'src/components/ts-components/SegmentedInput'
import Text from 'src/components/ts-components/Text'
import { ColorField } from '../../../components/FormikField'
import { OptionalColourGroup } from './OptionalColourGroup'
import { PresetChips } from './PresetChips'
import { SettingsCard } from './SettingsCard'

interface ThemeColoursProps {
  brand: PluginBrand
}

const MODE_OPTIONS = PLUGIN_THEME_MODE_OPTIONS.map(({ label, value }) => ({
  label: capitalizeWords(label),
  value,
}))

const SCOREBOARD_OPTIONS = PLUGIN_SCOREBOARD_OPTIONS.map(
  ({ label, value }) => ({ label: capitalizeWords(label), value })
)

/** The bracket's group follows the scoreboard style, as in the plugin's panel. */
const isBracketGroup = (group: PluginOptionalColourGroup) =>
  group.colours.some(({ name }) => name === 'bracketBackground')
const GROUPS_BEFORE_SCOREBOARD = PLUGIN_OPTIONAL_COLOUR_GROUPS.filter(
  (group) => !isBracketGroup(group)
)
const GROUPS_AFTER_SCOREBOARD =
  PLUGIN_OPTIONAL_COLOUR_GROUPS.filter(isBracketGroup)

/**
 * Mode, presets, the page and header colours (each unset until changed, and
 * sent once set), the optional colour groups and the scoreboard.
 */
export const ThemeColours = ({ brand }: ThemeColoursProps) => {
  const { values, setFieldValue } = useFormikContext<PluginEmbedSettings>()
  const { colours, branding } = values
  const defaults = pluginPageDefaults(brand, values)
  // Only the league's own colours can be hard to see; the defaults are the plugin's.
  const isPrimaryHidden =
    (colours.primary !== undefined || colours.headerBackground !== undefined) &&
    isPrimaryHardToSeeOnHeader({
      primary: colours.primary ?? defaults.primary,
      headerBackground: colours.headerBackground ?? defaults.headerBackground,
    })

  return (
    <>
      <SegmentedInput
        label={capitalizeWords('Mode')}
        options={MODE_OPTIONS}
        value={values.mode}
        onChange={(mode: PluginThemeMode) => setFieldValue('mode', mode)}
      />
      <PresetChips />
      {PLUGIN_PAGE_COLOUR_GROUPS.map((group) => (
        <SettingsCard
          key={group.caption}
          title={group.caption}
          description={group.description}
        >
          <div css={swatchGridStyle}>
            {group.colours.map(({ name, label }) => (
              <ColorField
                key={name}
                name={`colours.${name}`}
                label={capitalizeWords(label)}
                defaultColour={defaults[name]}
              />
            ))}
          </div>
        </SettingsCard>
      ))}
      {isPrimaryHidden ? (
        <Text variant='paragraphXSmall' color={colors.WARNING_300}>
          Your primary colour is hard to see on your header — the plugin will
          use the header text colour for tab underlines.
        </Text>
      ) : null}
      {GROUPS_BEFORE_SCOREBOARD.map((group) => (
        <OptionalColourGroup
          key={group.caption}
          group={group}
          pageDefaults={defaults}
        />
      ))}
      <SegmentedInput
        label={capitalizeWords('Scoreboard Style')}
        hint='The game page scoreboard: the sport photo, or the header colours'
        options={SCOREBOARD_OPTIONS}
        value={branding.scoreboard ?? 'photo'}
        onChange={(next) =>
          setFieldValue(
            'branding.scoreboard',
            next === 'header' ? 'header' : undefined
          )
        }
      />
      {GROUPS_AFTER_SCOREBOARD.map((group) => (
        <OptionalColourGroup
          key={group.caption}
          group={group}
          pageDefaults={defaults}
        />
      ))}
    </>
  )
}

const swatchGridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`
